import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import Chart from 'react-apexcharts';

import { PageContentPreloader, DefaultButton, NoItemsPanel } from 'common/components';

import { withTheme } from 'common/styling/theme';
import noPartnersImage from 'common/images/noPartners.svg';
import noLinks from 'common/images/noInfo.svg';

import { staticStyles, getDynamicStyles } from './style';

class PartnershipLinksBlock extends React.Component {
  constructor(props) {
    super(props);
    const { getPartnershipInfo } = props;

    getPartnershipInfo();

    this.state = {
      copied: false,
      chartShow: { partnerCode: -1, linkIndex: -1 },
      clicksByMonths: [],
    };
  }

  handleCopyClick = () => {
    this.setState({ copied: true });

    setTimeout(() => {
      this.setState({ copied: false });
    }, 3000);
  };

  handlePartnerLinkClicks = (partnerCode, partnerIndex, linkIndex) => {
    this.setState({ chartShow: { partnerCode, linkIndex } });

    const clicks = this.props.partnershipLinks[partnerIndex].links[linkIndex].clicks;
    const groupedClicks = {};

    clicks.forEach(click => {
      const key = moment(click.created).format('YYYY-MM');

      groupedClicks[key] = (groupedClicks[key] || 0) + 1;
    });

    const currentMonth = moment().startOf('month');
    const startMonth = moment().subtract(11, 'months').startOf('month');
    const allMonths = [];

    const monthCursor = startMonth.clone();

    while (monthCursor.isSameOrBefore(currentMonth)) {
      const key = monthCursor.format('YYYY-MM');

      allMonths.push({
        key,
        title: monthCursor.format('MMM YYYY'),
        count: groupedClicks[key] || 0,
      });

      monthCursor.add(1, 'month');
    }

    this.setState({
      clicksByMonths: allMonths,
    });

    if (JSON.stringify(this.state.chartShow) === JSON.stringify({ partnerCode, linkIndex })) {
      this.setState({ chartShow: { partnerCode: -1, linkIndex: -1 } });
    }
  };

  render() {
    const { partnershipLinks, partnershipInfoIsLoaded, intl, theme, partnershipConfig, referType } = this.props;

    const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

    const options = {
      plotOptions: {
        bar: {
          horizontal: false,
          distributed: true,
        },
      },
      stroke: {
        curve: 'straight',
      },
      colors: ['#0AA1E2'],
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: this.state.clicksByMonths.map(m => m.title),
      },
      legend: {
        horizontalAlign: 'left',
      },
    };

    const series = [
      {
        name: intl.formatMessage({ id: 'partnershipClicks' }),
        data: this.state.clicksByMonths.map(m => m.count),
      },
    ];

    const { defaultPartnerCodeParamName, partnerCodeParamNames } = partnershipConfig;

    const additionalParamNamesLinks = partnerCodeParamNames.filter(param => param !== defaultPartnerCodeParamName);

    return (
      <>
        <PageContentPreloader ready={partnershipInfoIsLoaded} type="line">
          <>
            {partnershipLinks.length ? (
              <div className="PartnershipInfoLinksPage">
                {partnershipLinks.map((partner, index) => (
                  <div key={index} className="PartnershipInfoLinks__item">
                    <FormattedMessage id="referralAccountLink">
                      {txt => (
                        <p className="PartnershipInfoLinks__partner-code">
                          {txt}: <span>{partner.partnerCode}</span>
                        </p>
                      )}
                    </FormattedMessage>

                    {partner.links.length ? (
                      <>
                        <div className="PartnershipInfoLinks__default-parameter">
                          <FormattedMessage
                            id={
                              referType === 'friend'
                                ? 'settingPartnershipDefaultFriendCodeParamNameTitle'
                                : 'settingPartnershipDefaultPartnerCodeParamNameTitle'
                            }>
                            {txt => <h3 className="PartnershipInfoLinks__title">{txt}</h3>}
                          </FormattedMessage>
                          {partner.links.map((link, i) => (
                            <div className="PartnershipInfoLinks__item-links" key={i}>
                              <div className="Link__inner">
                                <p className="Link__text">{`${link.link}?${defaultPartnerCodeParamName}=${partner.partnerCode}`}</p>
                                <FormattedMessage id="justCopy">
                                  {txt => (
                                    <CopyToClipboard
                                      text={`${link.link}?${defaultPartnerCodeParamName}=${partner.partnerCode}`}
                                      onCopy={this.handleCopyClick}>
                                      <button type="button" className="Copy-button__url">
                                        {txt}
                                      </button>
                                    </CopyToClipboard>
                                  )}
                                </FormattedMessage>
                              </div>

                              <DefaultButton
                                textId={
                                  JSON.stringify(this.state.chartShow) ===
                                  JSON.stringify({ partnerCode: partner.partnerCode, linkIndex: i })
                                    ? 'justHideStatistic'
                                    : 'justShowStatistic'
                                }
                                onClick={() => this.handlePartnerLinkClicks(partner.partnerCode, index, i)}
                                filled
                              />

                              <div className="PartnershipInfoLinks__chart-inner">
                                {JSON.stringify(this.state.chartShow) ===
                                  JSON.stringify({ partnerCode: partner.partnerCode, linkIndex: i }) && (
                                  <div className="PartnershipInfoLinks__chart">
                                    <FormattedMessage id="partnershipClicks">{txt => <h2>{txt}</h2>}</FormattedMessage>
                                    <Chart options={options} series={series} type="area" height="400" />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {additionalParamNamesLinks.length > 0 && (
                          <div>
                            <FormattedMessage id="settingPartnershipAdditionalPartnerCodeParamNamesTitle">
                              {txt => <h3 className="PartnershipInfoLinks__title">{txt}</h3>}
                            </FormattedMessage>
                            {partner.links.map((link, i) => (
                              <div key={i} className="PartnershipInfoLinks__item-additional-link-block">
                                {additionalParamNamesLinks.map((param, i) => (
                                  <div className="PartnershipInfoLinks__item-links" key={i}>
                                    <div className={classNames('Link__inner', 'Link__inner-additional')}>
                                      <p className="Link__text">{`${link.link}?${param}=${partner.partnerCode}`}</p>
                                      <FormattedMessage id="justCopy">
                                        {txt => (
                                          <CopyToClipboard
                                            text={`${link.link}?${param}=${partner.partnerCode}`}
                                            onCopy={this.handleCopyClick}>
                                            <button type="button" className="Copy-button__url">
                                              {txt}
                                            </button>
                                          </CopyToClipboard>
                                        )}
                                      </FormattedMessage>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="PartnershipNoInfoLinks">
                        <div className="PartnershipNoInfoLinks__image-inner">
                          <img src={noLinks} alt="No partnership links yet" />
                        </div>
                        <FormattedMessage id="noReferalLinkDescription">{txt => <p>{txt}</p>}</FormattedMessage>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <NoItemsPanel
                icon={noPartnersImage}
                captionSlug="noReferalLinkCaption"
                descriptionSlug="noReferalLinkDescription"
              />
            )}
          </>
        </PageContentPreloader>

        <div
          className={classNames('Copied__inner', {
            'Copied__inner-active': this.state.copied,
          })}>
          <FormattedMessage id="justCopied">{txt => <p>{txt}</p>}</FormattedMessage>
        </div>

        <style jsx global>
          {staticStyles}
        </style>
        <style jsx global>
          {dynamicStyles}
        </style>
      </>
    );
  }
}

PartnershipLinksBlock.propTypes = {
  getPartnershipInfo: PropTypes.func.isRequired,
  partnershipLinks: PropTypes.array.isRequired,
  partnershipInfoIsLoaded: PropTypes.bool.isRequired,
  partnershipConfig: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  theme: PropTypes.object,
  referType: PropTypes.string,
};

PartnershipLinksBlock.defaultProps = {
  theme: {},
  referType: 'partner',
};

const ThemedPartnershipLinksBlock = withTheme()(injectIntl(PartnershipLinksBlock));

export default connect(
  state => ({
    partnershipInfoIsLoaded: state.client.partnership.partnershipInfoIsLoaded,
    partnershipLinks: state.client.partnership.partnershipLinks,
    partnershipConfig: state.interfaceConfig.partnership,
  }),
  {}
)(ThemedPartnershipLinksBlock);
export { PartnershipLinksBlock };
